import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  FiPlus,
  FiCheckSquare,
  FiCheck,
  FiX,
  FiSend,
  FiSettings,
  FiArrowLeft,
  FiTrash2,
  FiRotateCcw,
  FiSlash,
} from 'react-icons/fi';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import LoadingButton from '../components/LoadingButton';
import ListSearchBar from '../components/ListSearchBar';
import Modal from '../components/Modal';
import { FadeIn } from '../components/motion';
import { formatDateOnly, formatLocalDate, formatLocalDateTime } from '../utils/formatTimestamp';
import formatMoney from '../utils/formatMoney';
import { orderStatusLabel } from '../utils/orderStatus';
import { taxIdLabel } from '../utils/taxDocumentFields';
import { queryKeys, staleTimes } from '../config/queryKeys';
import './Requisitions.css';
import { appAlert, appConfirm } from '../utils/appDialog';
import ListPager from '../components/ListPager';
import { useClientPager } from '../hooks/useClientPager';

const EMPTY_FORM = {
  customer_id: '',
  title: '',
  engagement_type: 'consulting',
  effective_date: '',
  end_date: '',
  term_months: '',
  estimated_value: '',
  currency: 'USD',
  scope_summary: '',
  deliverables: '',
  milestones: '',
  payment_terms: '',
  sla_notes: '',
  confidentiality_notes: '',
  non_solicit_months: '',
  data_processing_notes: '',
  msa_notes: '',
  governing_law: '',
  venue: '',
  customer_signatory_name: '',
  customer_signatory_title: '',
  customer_signatory_email: '',
  company_signatory_name: '',
  company_signatory_title: '',
  special_terms: '',
  internal_notes: '',
};

const STATUS_LABEL = {
  draft: 'Draft',
  review: 'Review',
  approval: 'Approval',
  approved: 'Approved',
  rejected: 'Rejected',
  void: 'Void',
  // legacy keys
  drafting: 'Draft',
  reviewing: 'Review',
  approving: 'Approval',
  executing: 'Approved',
  completed: 'Approved',
};

function statusLabel(status, stages = []) {
  const match = (stages || []).find((s) => s.stage_key === status || s.code === status);
  return match?.label || match?.name || STATUS_LABEL[status] || status;
}

const money = formatMoney;

function formFromDetail(d) {
  return {
    customer_id: d.customer_id ? String(d.customer_id) : '',
    title: d.title || '',
    engagement_type: d.engagement_type || 'consulting',
    effective_date: d.effective_date ? String(d.effective_date).slice(0, 10) : '',
    end_date: d.end_date ? String(d.end_date).slice(0, 10) : '',
    term_months: d.term_months ?? '',
    estimated_value: d.estimated_value ?? '',
    currency: d.currency || 'USD',
    scope_summary: d.scope_summary || '',
    deliverables: d.deliverables || '',
    milestones: d.milestones || '',
    payment_terms: d.payment_terms || '',
    sla_notes: d.sla_notes || '',
    confidentiality_notes: d.confidentiality_notes || '',
    non_solicit_months: d.non_solicit_months ?? '',
    data_processing_notes: d.data_processing_notes || '',
    msa_notes: d.msa_notes || '',
    governing_law: d.governing_law || '',
    venue: d.venue || '',
    customer_signatory_name: d.customer_signatory_name || '',
    customer_signatory_title: d.customer_signatory_title || '',
    customer_signatory_email: d.customer_signatory_email || '',
    company_signatory_name: d.company_signatory_name || '',
    company_signatory_title: d.company_signatory_title || '',
    special_terms: d.special_terms || '',
    internal_notes: d.internal_notes || '',
  };
}

const Requisitions = () => {
  const { user } = useAuth();
  const { hasAnyPermission, hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Mirrors canManage/canApprove/canView in backend/src/routes/requisitions.js.
  const canManage = hasAnyPermission(['requisition_manage', 'view_all_data']);
  const canApprove = hasPermission('requisition_approve');
  const canWorkflow = hasPermission('requisition_workflow_manage');
  const canView =
    hasAnyPermission(['tab_requisitions', 'requisition_view']) ||
    canManage ||
    canApprove ||
    canWorkflow;

  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [selectedId, setSelectedId] = useState(searchParams.get('id') || null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  // Snapshot of the form as opened, so closing can warn about unsaved edits.
  const pristineFormRef = useRef(EMPTY_FORM);
  const [comment, setComment] = useState('');
  const [showWorkflow, setShowWorkflow] = useState(false);
  const [assigneeDraft, setAssigneeDraft] = useState({});
  const [packageToAdd, setPackageToAdd] = useState('');

  useEffect(() => {
    setSelectedId(searchParams.get('id') || null);
  }, [searchParams]);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const setSelectionParam = (id) => {
    const params = new URLSearchParams(searchParams);
    if (id) params.set('id', String(id));
    else params.delete('id');
    setSearchParams(params, { replace: true });
  };

  const openRecord = (id) => {
    setSelectedId(String(id));
    setSelectionParam(id);
  };

  const backToList = () => {
    setSelectedId(null);
    setSelectionParam(null);
  };

  const { data: list = [], isLoading, isError: listError, error: listErrorObj } = useQuery(
    ['requisitions', statusFilter, searchDebounced],
    () =>
      api
        .get('/requisitions', {
          params: {
            status: statusFilter && statusFilter !== 'all' ? statusFilter : undefined,
            include_void: statusFilter === 'all' ? 1 : undefined,
            q: searchDebounced || undefined,
          },
        })
        .then((r) => {
          const data = r.data;
          if (Array.isArray(data)) return data;
          if (Array.isArray(data?.requisitions)) return data.requisitions;
          if (Array.isArray(data?.items)) return data.items;
          return [];
        }),
    { keepPreviousData: true }
  );

  const {
    pageItems: pagedList,
    pagination: listPagination,
    setPage: setListPage,
  } = useClientPager(list, {
    resetKey: `${statusFilter}|${searchDebounced}`,
  });

  const { data: detail, isLoading: detailLoading } = useQuery(
    ['requisition', selectedId],
    () => api.get(`/requisitions/${selectedId}`).then((r) => r.data),
    { enabled: Boolean(selectedId) }
  );

  const { data: quotationChoices = [] } = useQuery(
    ['requisition-quotation-choices', selectedId],
    () => api.get(`/requisitions/${selectedId}/quotation-choices`).then((r) => r.data || []),
    { enabled: Boolean(selectedId), staleTime: 15_000 }
  );

  const linkQuotationMutation = useMutation(
    ({ id, salesDocumentId }) =>
      api.put(`/requisitions/${id}/quotation`, { sales_document_id: salesDocumentId }),
    {
      onSuccess: (_res, vars) => {
        queryClient.invalidateQueries(['requisition', vars.id]);
        queryClient.invalidateQueries(['requisition-quotation-choices', vars.id]);
        queryClient.invalidateQueries(queryKeys.salesDocuments());
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const { data: customers = [] } = useQuery(
    queryKeys.serviceCustomers,
    () => api.get('/services/customers').then((r) => r.data),
    {
      enabled: canManage || showForm,
      staleTime: staleTimes.reference,
    }
  );

  const { data: workflow } = useQuery(
    queryKeys.requisitionWorkflow,
    () => api.get('/requisitions/workflow').then((r) => r.data),
    { staleTime: staleTimes.reference }
  );

  const { data: allPackages = [] } = useQuery(
    queryKeys.servicePackages,
    () => api.get('/services/packages').then((r) => r.data || []),
    {
      enabled: Boolean(selectedId),
      staleTime: staleTimes.reference,
    }
  );
  const catalogPackages = useMemo(
    () => (allPackages || []).filter((p) => p.is_active !== 0),
    [allPackages]
  );

  const workflowStages = useMemo(() => workflow?.stages || [], [workflow?.stages]);
  const labelFor = (status) => statusLabel(status, workflowStages);

  const { data: users = [] } = useQuery(
    queryKeys.usersPicker('requisition-assignees'),
    () =>
      api
        .get('/users')
        .then((r) => (r.data || []).filter((u) => u.is_active && u.role !== 'candidate')),
    { enabled: showWorkflow && canWorkflow, staleTime: staleTimes.lists }
  );

  const stageKeySignature = useMemo(
    () => workflowStages.map((s) => s.stage_key).join('|'),
    [workflowStages]
  );

  // Seed only when the modal opens or the stage set itself changes — refetches
  // return a new workflow object and would clobber unsaved multi-selects.
  useEffect(() => {
    if (!showWorkflow) return;
    const draft = {};
    for (const s of workflowStages) {
      draft[s.stage_key] = (s.assignees || []).map((a) => String(a.user_id));
    }
    setAssigneeDraft(draft);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on stage identity, not payload identity
  }, [showWorkflow, stageKeySignature]);

  const saveMutation = useMutation(
    (payload) =>
      editingId
        ? api.put(`/requisitions/${editingId}`, payload)
        : api.post('/requisitions', payload),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries('requisitions');
        queryClient.invalidateQueries(['requisition']);
        pristineFormRef.current = form;
        setShowForm(false);
        setEditingId(null);
        const id = res.data?.id;
        if (id) {
          setSelectedId(String(id));
          setSelectionParam(id);
        }
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const transitionMutation = useMutation(
    ({ id, action, comment: c }) =>
      api.post(`/requisitions/${id}/transition`, { action, comment: c }),
    {
      onSuccess: (res, variables) => {
        queryClient.invalidateQueries('requisitions');
        queryClient.invalidateQueries(['requisition', selectedId]);
        queryClient.invalidateQueries('purchase-orders');
        setComment('');
        const po = res.data?.purchase_order;
        if (po?.id && variables?.action !== 'void') {
          navigate(`/orders/${po.id}`);
        }
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const addPackageMutation = useMutation(
    ({ id, package_id }) => api.post(`/requisitions/${id}/packages`, { package_id }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['requisition', selectedId]);
        queryClient.invalidateQueries('requisitions');
        setPackageToAdd('');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const removePackageMutation = useMutation(
    ({ id, lineId }) => api.delete(`/requisitions/${id}/packages/${lineId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['requisition', selectedId]);
        queryClient.invalidateQueries('requisitions');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const saveAssigneesMutation = useMutation(
    ({ stage_key, user_ids }) =>
      api.put('/requisitions/workflow/assignees', { stage_key, user_ids }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['requisition-workflow']);
        void appAlert('Workflow assignees saved.');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const formDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(pristineFormRef.current),
    [form]
  );

  const openForm = (nextForm, id = null) => {
    pristineFormRef.current = nextForm;
    setEditingId(id);
    setForm(nextForm);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const openCreate = () => {
    openForm({ ...EMPTY_FORM, customer_id: customers[0] ? String(customers[0].id) : '' });
  };

  const openEdit = () => {
    if (!detail) return;
    openForm(formFromDetail(detail), detail.id);
  };

  const submitForm = (e) => {
    e.preventDefault();
    if (!form.customer_id) {
      void appAlert('Select a customer before creating a requisition.');
      return;
    }
    if (!form.title.trim()) {
      void appAlert('Title is required.');
      return;
    }
    saveMutation.mutate({
      ...form,
      customer_id: Number(form.customer_id),
      term_months: form.term_months === '' ? null : Number(form.term_months),
      estimated_value: form.estimated_value === '' ? null : Number(form.estimated_value),
      non_solicit_months:
        form.non_solicit_months === '' ? null : Number(form.non_solicit_months),
    });
  };

  const stages = useMemo(() => {
    const fromWorkflow = (workflowStages || [])
      .filter(
        (s) => !s.is_rejected && !s.is_completed && s.is_active !== false && s.stage_key !== 'void'
      )
      .map((s) => s.stage_key);
    return fromWorkflow.length ? fromWorkflow : ['draft', 'review', 'approval'];
  }, [workflowStages]);
  const activeStageIdx = stages.indexOf(detail?.status);
  const isDraftStatus =
    detail &&
    (detail.status === 'draft' ||
      detail.status === 'drafting' ||
      workflowStages.find((s) => s.stage_key === detail.status)?.is_initial);
  const isEditableStatus =
    isDraftStatus || detail?.status === 'rejected';
  const isVoidStatus = detail?.status === 'void';

  /**
   * Approval buttons belong to the people the stage is waiting on. Workflow
   * managers keep access, and stages with no assignees fall back to the plain
   * requisition_approve grant so an unconfigured flow can still advance.
   */
  const canActOnStage = useMemo(() => {
    if (!canApprove) return false;
    if (canWorkflow) return true;
    const assignees = detail?.stage_assignees || [];
    if (!assignees.length) return true;
    const me = String(user?.id ?? '');
    if (!me) return false;
    const isMe = (a) => String(a.id ?? a.user_id ?? '') === me;
    return assignees.some(isMe) || (detail?.pending_approvers || []).some(isMe);
  }, [canApprove, canWorkflow, detail, user?.id]);
  const filterStatuses = useMemo(() => {
    const list = (workflowStages || []).filter(
      (s) => s.is_active !== false && s.stage_key !== 'void'
    );
    const mapped = list.length
      ? list.map((s) => ({ key: s.stage_key, label: s.label || s.name }))
      : Object.entries(STATUS_LABEL)
          .filter(([key]) => key !== 'void')
          .map(([key, label]) => ({ key, label }));
    mapped.push({ key: 'void', label: 'Void' });
    return mapped;
  }, [workflowStages]);

  return (
    <div className="requisitions-page list-page">
      {!canView ? (
        <div className="error" role="alert">
          You do not have permission to view requisitions.
        </div>
      ) : (
        <>
      <FadeIn className="page-header">
        <div className="page-header-text">
          <h1>
            <FiCheckSquare aria-hidden /> Requisitions
          </h1>
          <p className="page-subtitle">
            Customer work orders — attach service packages, set dates, then route through review and
            approval. On approval a Client PO is drafted under Revenue → Client POs.
          </p>
        </div>
        <div className="page-toolbar list-page-header-actions">
          {canWorkflow && (
            <button type="button" className="btn btn-secondary" onClick={() => setShowWorkflow(true)}>
              <FiSettings /> Workflow
            </button>
          )}
          {canManage && (
            <button type="button" className="btn btn-primary" onClick={openCreate} disabled={!customers.length}>
              <FiPlus /> New requisition
            </button>
          )}
        </div>
      </FadeIn>

      {!customers.length && canManage ? (
        <p className="req-banner">
          Create a customer first under Customers — every requisition must be linked to a customer.
        </p>
      ) : null}

      {!selectedId ? (
        <section className="svc-panel req-table-panel">
          <div className="list-filters-panel list-toolbar-panel">
            <div className="filter-row list-toolbar-row">
              <div className="filter-group filter-group--wide list-toolbar-search">
                <ListSearchBar
                  id="req-filter-search"
                  value={search}
                  onChange={setSearch}
                  placeholder="Search title or customer…"
                />
              </div>
              <div className="filter-group">
                <label htmlFor="req-filter-status">Status</label>
                <select
                  id="req-filter-status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All statuses</option>
                  <option value="all">All statuses, including void</option>
                  {filterStatuses.map(({ key, label }) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {listError ? (
            <div className="error" role="alert">
              {listErrorObj?.response?.data?.error ||
                listErrorObj?.message ||
                'Failed to load requisitions.'}
            </div>
          ) : isLoading ? (
            <p className="svc-empty">Loading…</p>
          ) : list.length === 0 ? (
            <p className="svc-empty">
              {statusFilter === 'void'
                ? 'No void requisitions.'
                : statusFilter
                  ? 'No requisitions match this filter.'
                  : 'No requisitions yet.'}
            </p>
          ) : (
            <table className="req-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Value</th>
                  <th>Client PO</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {pagedList.map((row) => (
                  <tr
                    key={row.id}
                    tabIndex={0}
                    role="link"
                    aria-label={`Open requisition ${row.title}`}
                    onClick={() => openRecord(row.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openRecord(row.id);
                      }
                    }}
                  >
                    <td className="req-cell-title">{row.title}</td>
                    <td>{row.customer_billing_name || '—'}</td>
                    <td>
                      <span className={`svc-status svc-status--${row.status}`}>
                        {labelFor(row.status)}
                      </span>
                    </td>
                    <td className="money-cell">
                      {row.estimated_value != null
                        ? money(row.estimated_value, row.currency)
                        : '—'}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {row.purchase_order_id ? (
                        <Link to={`/orders/${row.purchase_order_id}`}>
                          {row.purchase_order_number || `#${row.purchase_order_id}`}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      {row.modifiedon_timestamp
                        ? formatLocalDate(row.modifiedon_timestamp)
                        : row.createdon_timestamp
                          ? formatLocalDate(row.createdon_timestamp)
                          : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!listError && !isLoading && list.length > 0 ? (
            <ListPager
              page={listPagination.page}
              pageCount={listPagination.pageCount}
              total={listPagination.total}
              limit={listPagination.limit}
              onPageChange={setListPage}
            />
          ) : null}
        </section>
      ) : (
        <section className="svc-panel req-detail-view">
          {detailLoading ? (
            <p className="svc-empty">Loading…</p>
          ) : !detail ? (
            <>
              <button type="button" className="btn btn-secondary btn-sm" onClick={backToList}>
                <FiArrowLeft /> Back to list
              </button>
              <p className="svc-empty">Not found.</p>
            </>
          ) : (
            <>
              <div className="req-detail-top">
                <div>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={backToList}>
                    <FiArrowLeft /> Back to list
                  </button>
                  <h2>{detail.title}</h2>
                  <p className="svc-panel-meta">
                    {detail.customer_billing_name}
                    {detail.customer_legal_name && detail.customer_legal_name !== detail.customer_billing_name
                      ? ` · Legal: ${detail.customer_legal_name}`
                      : ''}
                  </p>
                </div>
                <div className="req-detail-actions">
                  <span className={`svc-status svc-status--${detail.status}`}>
                    {labelFor(detail.status)}
                  </span>
                  {canManage && isEditableStatus && (
                    <button type="button" className="btn btn-secondary btn-sm" onClick={openEdit}>
                      Edit
                    </button>
                  )}
                </div>
              </div>

              <ol className={`req-stage-track${isVoidStatus ? ' is-void' : ''}`}>
                {stages.map((s, idx) => (
                  <li
                    key={s}
                    className={`req-stage${detail.status === s ? ' is-current' : ''}${
                      activeStageIdx > idx || detail.status === 'approved' || detail.status === 'completed'
                        ? ' is-done'
                        : ''
                    }${detail.status === 'rejected' && idx > 0 ? ' is-rejected' : ''}`}
                  >
                    {labelFor(s)}
                  </li>
                ))}
              </ol>

              <div className="req-section req-quote-link">
                <h3>Quotation</h3>
                {canManage ? (
                  <div className="form-group">
                    <label htmlFor="req-quotation">Linked quotation</label>
                    <select
                      id="req-quotation"
                      value={detail.quotation?.id ? String(detail.quotation.id) : ''}
                      onChange={(e) =>
                        linkQuotationMutation.mutate({
                          id: detail.id,
                          salesDocumentId: e.target.value ? Number(e.target.value) : null,
                        })
                      }
                      disabled={linkQuotationMutation.isLoading || isVoidStatus}
                    >
                      <option value="">Not linked</option>
                      {(quotationChoices || [])
                        .concat(
                          detail.quotation &&
                            !(quotationChoices || []).some(
                              (quote) => String(quote.id) === String(detail.quotation.id)
                            )
                            ? [detail.quotation]
                            : []
                        )
                        .map((quote) => (
                        <option key={quote.id} value={quote.id}>
                          {quote.number}
                          {quote.client_name ? ` · ${quote.client_name}` : ''}
                          {quote.total != null ? ` · ${money(quote.total, quote.currency)}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                {detail.quotation ? (
                  <div className="req-quote-pop" role="region" aria-label="Linked quotation">
                    <div className="req-quote-pop-head">
                      <div>
                        <strong>{detail.quotation.number}</strong>
                        <span className="req-quote-pop-status">{detail.quotation.status}</span>
                      </div>
                      <Link to={`/sales/documents/${detail.quotation.id}`}>Open quotation</Link>
                    </div>
                    <p className="req-quote-pop-value">
                      Total {money(detail.quotation.total, detail.quotation.currency)}
                    </p>
                    <dl className="req-quote-pop-dl">
                      <div>
                        <dt>Client</dt>
                        <dd>{detail.quotation.client_name || '—'}</dd>
                      </div>
                      {detail.quotation.seller_snapshot &&
                      (detail.quotation.seller_snapshot.legal_name ||
                        detail.quotation.seller_snapshot.trade_name ||
                        (detail.quotation.seller_snapshot.tax_ids || []).some((t) => t.value)) ? (
                        <div>
                          <dt>Seller</dt>
                          <dd>
                            {detail.quotation.seller_snapshot.legal_name ||
                              detail.quotation.seller_snapshot.trade_name ||
                              '—'}
                            {(detail.quotation.seller_snapshot.tax_ids || [])
                              .filter((t) => t.value)
                              .map((t) => ` · ${taxIdLabel(t.type)} ${t.value}`)
                              .join('')}
                          </dd>
                        </div>
                      ) : null}
                      <div>
                        <dt>Subtotal</dt>
                        <dd>{money(detail.quotation.subtotal, detail.quotation.currency)}</dd>
                      </div>
                      <div>
                        <dt>Tax</dt>
                        <dd>{money(detail.quotation.tax_amount, detail.quotation.currency)}</dd>
                      </div>
                      <div>
                        <dt>Valid until</dt>
                        <dd>{formatDateOnly(detail.quotation.valid_until)}</dd>
                      </div>
                    </dl>
                  </div>
                ) : (
                  <p className="svc-empty">
                    No quotation linked.
                    {canManage && !isVoidStatus ? (
                      <>
                        {' '}
                        <Link to={`/sales/documents/new?type=quotation&requisition_id=${detail.id}`}>
                          Create a quotation
                        </Link>{' '}
                        from this requisition.
                      </>
                    ) : null}
                  </p>
                )}
              </div>

              {detail.purchase_order ? (
                <div className="req-related-links">
                  <Link
                    className="req-related-chip"
                    to={`/orders/${detail.purchase_order.id}`}
                  >
                    Client PO <strong>{detail.purchase_order.number}</strong>
                    <span className={`svc-status svc-status--${detail.purchase_order.status}`}>
                      {orderStatusLabel(detail.purchase_order.status)}
                    </span>
                  </Link>
                </div>
              ) : null}

              <div className="req-sections">
                <div className="req-section">
                  <h3>Service packages</h3>
                  {(detail.packages || []).length === 0 ? (
                    <p className="svc-empty">No packages attached yet.</p>
                  ) : (
                    <ul className="req-list-items">
                      {(detail.packages || []).map((p) => (
                        <li key={p.id} className="req-list-item" style={{ cursor: 'default' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                            <div>
                              <strong>{p.package_name}</strong>
                              <span className="svc-panel-meta" style={{ display: 'block' }}>
                                Qty {p.quantity} · {money(p.unit_price, p.currency)} each
                              </span>
                            </div>
                            {canManage && isDraftStatus && (
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                title="Remove package"
                                aria-label={`Remove package ${p.package_name}`}
                                onClick={() =>
                                  removePackageMutation.mutate({ id: detail.id, lineId: p.id })
                                }
                              >
                                <FiTrash2 />
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {canManage && isDraftStatus && (
                    <div className="req-filters" style={{ marginTop: '0.75rem' }}>
                      <label htmlFor="req-add-package" className="visually-hidden">
                        Add service package
                      </label>
                      <select
                        id="req-add-package"
                        value={packageToAdd}
                        onChange={(e) => setPackageToAdd(e.target.value)}
                        aria-label="Add service package"
                      >
                        <option value="">Add service package…</option>
                        {catalogPackages.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} · {money(p.package_price, p.currency || 'USD')}
                          </option>
                        ))}
                      </select>
                      <LoadingButton
                        type="button"
                        className="btn btn-secondary btn-sm"
                        loading={addPackageMutation.isLoading}
                        disabled={!packageToAdd}
                        onClick={() =>
                          addPackageMutation.mutate({
                            id: detail.id,
                            package_id: Number(packageToAdd),
                          })
                        }
                      >
                        <FiPlus /> Add
                      </LoadingButton>
                    </div>
                  )}
                </div>

                <div className="req-section">
                  <h3>Overview</h3>
                  <dl className="req-dl">
                    <div><dt>Type</dt><dd>{detail.engagement_type || '—'}</dd></div>
                    <div><dt>Effective</dt><dd>{formatDateOnly(detail.effective_date)}</dd></div>
                    <div><dt>End</dt><dd>{formatDateOnly(detail.end_date)}</dd></div>
                    <div><dt>Value</dt><dd className="money-text">{detail.estimated_value != null ? money(detail.estimated_value, detail.currency) : '—'}</dd></div>
                    <div><dt>Term (months)</dt><dd>{detail.term_months ?? '—'}</dd></div>
                  </dl>
                </div>

                <div className="req-section">
                  <h3>Scope</h3>
                  <p><strong>Summary</strong><br />{detail.scope_summary || '—'}</p>
                  <p><strong>Deliverables</strong><br />{detail.deliverables || '—'}</p>
                  <p><strong>Milestones</strong><br />{detail.milestones || '—'}</p>
                </div>

                <div className="req-section">
                  <h3>Commercial</h3>
                  <p><strong>Payment terms</strong><br />{detail.payment_terms || '—'}</p>
                  <p><strong>Service levels</strong><br />{detail.sla_notes || '—'}</p>
                </div>

                <div className="req-section">
                  <h3>Terms</h3>
                  <p><strong>Confidentiality</strong><br />{detail.confidentiality_notes || '—'}</p>
                  <p><strong>Non-solicit (months)</strong><br />{detail.non_solicit_months ?? '—'}</p>
                  <p><strong>Data processing</strong><br />{detail.data_processing_notes || '—'}</p>
                  <p><strong>Agreement notes</strong><br />{detail.msa_notes || '—'}</p>
                  <dl className="req-dl">
                    <div><dt>Governing law</dt><dd>{detail.governing_law || '—'}</dd></div>
                    <div><dt>Venue</dt><dd>{detail.venue || '—'}</dd></div>
                  </dl>
                  {detail.special_terms ? (
                    <p><strong>Special terms</strong><br />{detail.special_terms}</p>
                  ) : null}
                </div>

                <div className="req-section">
                  <h3>Parties</h3>
                  <dl className="req-dl">
                    <div>
                      <dt>Customer contact</dt>
                      <dd>
                        {detail.customer_signatory_name || '—'}
                        {detail.customer_signatory_title ? ` · ${detail.customer_signatory_title}` : ''}
                        {detail.customer_signatory_email ? ` · ${detail.customer_signatory_email}` : ''}
                      </dd>
                    </div>
                    <div>
                      <dt>Company contact</dt>
                      <dd>
                        {detail.company_signatory_name || '—'}
                        {detail.company_signatory_title ? ` · ${detail.company_signatory_title}` : ''}
                      </dd>
                    </div>
                  </dl>
                </div>

                {detail.internal_notes ? (
                  <div className="req-section">
                    <h3>Notes</h3>
                    <p>{detail.internal_notes}</p>
                  </div>
                ) : null}
              </div>

              {(detail.stage_assignees || []).length > 0 && (
                <p className="svc-panel-meta">
                  Assignees (AND gate — all must approve):{' '}
                  {detail.stage_assignees
                    .map((a) => {
                      const approved = (detail.stage_approvals || []).some(
                        (x) => Number(x.user_id) === Number(a.id)
                      );
                      return `${a.first_name} ${a.last_name}${
                        a.from_customer ? ' (customer)' : ''
                      }${approved ? ' ✓' : ''}`;
                    })
                    .join(', ')}
                  {(detail.pending_approvers || []).length > 0
                    ? ` · waiting on ${detail.pending_approvers.length}`
                    : ''}
                </p>
              )}

              {isVoidStatus ? (
                <p className="svc-panel-meta">
                  This requisition is void. It stays off the default list until the status filter
                  includes void.
                </p>
              ) : (
              <div className="req-workflow-actions">
                <textarea
                  rows={2}
                  placeholder="Comment (optional)"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
                <div className="req-workflow-buttons">
                  {canManage && isDraftStatus && (
                    <LoadingButton
                      type="button"
                      className="btn btn-primary btn-sm"
                      icon={FiSend}
                      loading={transitionMutation.isLoading}
                      onClick={() =>
                        transitionMutation.mutate({
                          id: detail.id,
                          action: 'submit',
                          comment,
                        })
                      }
                    >
                      Submit for review
                    </LoadingButton>
                  )}
                  {canActOnStage &&
                    (() => {
                      const st = workflowStages.find((s) => s.stage_key === detail.status);
                      const actionable =
                        st?.email_actions ||
                        ['review', 'approval', 'reviewing', 'approving'].includes(detail.status) ||
                        (st && !st.is_initial && !st.is_completed && !st.is_rejected);
                      return actionable;
                    })() && (
                    <>
                      <LoadingButton
                        type="button"
                        className="btn btn-primary btn-sm"
                        icon={FiCheck}
                        loading={transitionMutation.isLoading}
                        onClick={() =>
                          transitionMutation.mutate({
                            id: detail.id,
                            action: 'approve',
                            comment,
                          })
                        }
                      >
                        Approve
                        {(detail.pending_approvers || []).length > 0
                          ? ` (${(detail.stage_approvals || []).length}/${
                              (detail.stage_assignees || []).length || '?'
                            })`
                          : ''}
                      </LoadingButton>
                      <LoadingButton
                        type="button"
                        className="btn btn-danger btn-sm"
                        icon={FiX}
                        loading={transitionMutation.isLoading}
                        onClick={() =>
                          transitionMutation.mutate({
                            id: detail.id,
                            action: 'reject',
                            comment,
                          })
                        }
                      >
                        Reject
                      </LoadingButton>
                      <LoadingButton
                        type="button"
                        className="btn btn-secondary btn-sm"
                        icon={FiArrowLeft}
                        loading={transitionMutation.isLoading}
                        onClick={() =>
                          transitionMutation.mutate({
                            id: detail.id,
                            action: 'send_back',
                            comment,
                          })
                        }
                      >
                        Send back
                      </LoadingButton>
                    </>
                  )}
                  {canManage && !isDraftStatus && (
                    <LoadingButton
                      type="button"
                      className="btn btn-danger btn-sm"
                      icon={FiRotateCcw}
                      loading={transitionMutation.isLoading}
                      onClick={async () => {
                        if (
                          !await appConfirm(
                            'Revoke this requisition? It will return to Draft, clear approvals, and revoke any linked Client PO (voiding its invoice so a new one can be generated later).'
                          )
                        ) {
                          return;
                        }
                        transitionMutation.mutate({
                          id: detail.id,
                          action: 'revoke',
                          comment,
                        });
                      }}
                    >
                      Revoke
                    </LoadingButton>
                  )}
                  {canManage && (
                    <LoadingButton
                      type="button"
                      className="btn btn-danger btn-sm"
                      icon={FiSlash}
                      loading={transitionMutation.isLoading}
                      onClick={async () => {
                        if (
                          !await appConfirm(
                            'Void this requisition? It will leave the default list. A linked Client PO that has moved past draft will be revoked and its invoice voided.'
                          )
                        ) {
                          return;
                        }
                        transitionMutation.mutate({
                          id: detail.id,
                          action: 'void',
                          comment,
                        });
                      }}
                    >
                      Void
                    </LoadingButton>
                  )}
                </div>
              </div>
              )}

              {(detail.events || []).length > 0 && (
                <div className="req-section">
                  <h3>History</h3>
                  <ul className="req-events">
                    {detail.events.map((ev) => (
                      <li key={ev.id}>
                        <strong>{ev.action}</strong> {ev.from_status || '—'} → {ev.to_status}
                        {ev.actor_first_name
                          ? ` · ${ev.actor_first_name} ${ev.actor_last_name}`
                          : ''}
                        <span className="svc-panel-meta">
                          {formatLocalDateTime(ev.createdon_timestamp)}
                          {ev.comment ? ` · ${ev.comment}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </section>
      )}

      <Modal
        open={showForm}
        onClose={closeForm}
        confirmClose={formDirty && 'Discard unsaved requisition changes?'}
        contentClassName="modal-content--wide"
        ariaLabel={editingId ? 'Edit requisition' : 'New requisition'}
      >
        <h2>{editingId ? 'Edit requisition' : 'New requisition'}</h2>
        <form onSubmit={submitForm} className="req-form">
          <div className="svc-form-grid svc-form-grid--2">
            <div className="form-group">
              <label htmlFor="req-form-customer">Customer *</label>
              <select
                id="req-form-customer"
                value={form.customer_id}
                onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                required
              >
                <option value="">Select customer…</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.billing_name}
                    {c.division ? ` · ${c.division}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="req-form-title">Title *</label>
              <input
                id="req-form-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
            </div>
          </div>

          <h3 className="req-form-heading">Overview</h3>
          <div className="svc-form-grid svc-form-grid--4">
            <div className="form-group">
              <label htmlFor="req-form-type">Type</label>
              <select
                id="req-form-type"
                value={form.engagement_type}
                onChange={(e) => setForm({ ...form, engagement_type: e.target.value })}
              >
                <option value="consulting">Consulting</option>
                <option value="staff_augmentation">Staff augmentation</option>
                <option value="managed_service">Managed service</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="req-form-effective">Effective date *</label>
              <input
                id="req-form-effective"
                type="date"
                value={form.effective_date}
                onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="req-form-end">End date *</label>
              <input
                id="req-form-end"
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Est. value</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.estimated_value}
                onChange={(e) => setForm({ ...form, estimated_value: e.target.value })}
              />
            </div>
          </div>
          <div className="svc-form-grid svc-form-grid--2">
            <div className="form-group">
              <label>Term (months)</label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={form.term_months}
                onChange={(e) => setForm({ ...form, term_months: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Currency</label>
              <input
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                placeholder="USD"
              />
            </div>
          </div>

          <h3 className="req-form-heading">Scope</h3>
          <div className="form-group">
            <label>Scope summary</label>
            <textarea
              rows={3}
              value={form.scope_summary}
              onChange={(e) => setForm({ ...form, scope_summary: e.target.value })}
            />
          </div>
          <div className="svc-form-grid svc-form-grid--2">
            <div className="form-group">
              <label>Deliverables</label>
              <textarea
                rows={3}
                value={form.deliverables}
                onChange={(e) => setForm({ ...form, deliverables: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Milestones</label>
              <textarea
                rows={3}
                value={form.milestones}
                onChange={(e) => setForm({ ...form, milestones: e.target.value })}
              />
            </div>
          </div>

          <h3 className="req-form-heading">Commercial</h3>
          <div className="svc-form-grid svc-form-grid--2">
            <div className="form-group">
              <label>Payment terms</label>
              <textarea
                rows={2}
                value={form.payment_terms}
                onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Service levels</label>
              <textarea
                rows={2}
                value={form.sla_notes}
                onChange={(e) => setForm({ ...form, sla_notes: e.target.value })}
              />
            </div>
          </div>

          <h3 className="req-form-heading">Terms</h3>
          <div className="form-group">
            <label>Confidentiality</label>
            <textarea
              rows={3}
              value={form.confidentiality_notes}
              onChange={(e) => setForm({ ...form, confidentiality_notes: e.target.value })}
            />
          </div>
          <div className="svc-form-grid svc-form-grid--2">
            <div className="form-group">
              <label>Non-solicit (months)</label>
              <input
                type="number"
                min="0"
                value={form.non_solicit_months}
                onChange={(e) => setForm({ ...form, non_solicit_months: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Data processing</label>
              <textarea
                rows={2}
                value={form.data_processing_notes}
                onChange={(e) => setForm({ ...form, data_processing_notes: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Agreement notes</label>
            <textarea
              rows={3}
              value={form.msa_notes}
              onChange={(e) => setForm({ ...form, msa_notes: e.target.value })}
            />
          </div>
          <div className="svc-form-grid svc-form-grid--2">
            <div className="form-group">
              <label>Governing law</label>
              <input
                value={form.governing_law}
                onChange={(e) => setForm({ ...form, governing_law: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Venue</label>
              <input
                value={form.venue}
                onChange={(e) => setForm({ ...form, venue: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Special terms</label>
            <textarea
              rows={2}
              value={form.special_terms}
              onChange={(e) => setForm({ ...form, special_terms: e.target.value })}
            />
          </div>

          <h3 className="req-form-heading">Parties</h3>
          <div className="svc-form-grid svc-form-grid--3">
            <div className="form-group">
              <label>Customer contact</label>
              <input
                value={form.customer_signatory_name}
                onChange={(e) => setForm({ ...form, customer_signatory_name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Title</label>
              <input
                value={form.customer_signatory_title}
                onChange={(e) => setForm({ ...form, customer_signatory_title: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={form.customer_signatory_email}
                onChange={(e) => setForm({ ...form, customer_signatory_email: e.target.value })}
              />
            </div>
          </div>
          <div className="svc-form-grid svc-form-grid--2">
            <div className="form-group">
              <label>Company contact</label>
              <input
                value={form.company_signatory_name}
                onChange={(e) => setForm({ ...form, company_signatory_name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Title</label>
              <input
                value={form.company_signatory_title}
                onChange={(e) => setForm({ ...form, company_signatory_title: e.target.value })}
              />
            </div>
          </div>

          <h3 className="req-form-heading">Notes</h3>
          <div className="form-group">
            <label>Internal notes</label>
            <textarea
              rows={2}
              value={form.internal_notes}
              onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={closeForm}>
              Cancel
            </button>
            <LoadingButton
              type="submit"
              className="btn btn-primary"
              loading={saveMutation.isLoading}
            >
              {editingId ? 'Save' : 'Create'}
            </LoadingButton>
          </div>
        </form>
      </Modal>

      <Modal open={showWorkflow} onClose={() => setShowWorkflow(false)} ariaLabel="Requisition workflow">
        <h2>Approval workflow</h2>
        <p className="form-hint">
          Optional company-wide assignees per stage. When assignees are set, Review/Approval use an
          AND gate — every assignee must approve before the stage advances. Each customer’s Internal
          contacts are always included automatically. When a requisition enters a notify stage,
          assignees receive email with Approve / Reject links (if Email actions is enabled on the
          status). Staff also see in-app notifications.
        </p>
        {(workflow?.stages || []).map((stage) => (
          <div key={stage.stage_key} className="req-workflow-stage">
            <h3>
              {stage.label}
              {!stage.notify_assignees ? ' (no notifications)' : ''}
            </h3>
            <p className="svc-panel-meta">{stage.description}</p>
            {stage.notify_assignees ? (
              <>
                <select
                  multiple
                  size={Math.min(6, Math.max(3, users.length || 3))}
                  value={assigneeDraft[stage.stage_key] || []}
                  onChange={(e) => {
                    const selected = [...e.target.selectedOptions].map((o) => o.value);
                    setAssigneeDraft((prev) => ({ ...prev, [stage.stage_key]: selected }));
                  }}
                >
                  {users.map((u) => (
                    <option key={u.id} value={String(u.id)}>
                      {u.first_name} {u.last_name} ({u.role})
                    </option>
                  ))}
                </select>
                <LoadingButton
                  type="button"
                  className="btn btn-secondary btn-sm"
                  loading={saveAssigneesMutation.isLoading}
                  onClick={() =>
                    saveAssigneesMutation.mutate({
                      stage_key: stage.stage_key,
                      user_ids: (assigneeDraft[stage.stage_key] || []).map(Number),
                    })
                  }
                >
                  Save {stage.label} assignees
                </LoadingButton>
              </>
            ) : (
              <p className="svc-panel-meta">Authors work in draft; no assignee notifications.</p>
            )}
          </div>
        ))}
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={() => setShowWorkflow(false)}>
            Close
          </button>
        </div>
      </Modal>
        </>
      )}
    </div>
  );
};

export default Requisitions;
